import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { fetchSection } from '@theme/section-renderer';
import { showToast } from '@theme/toast-message';
import { debounce, isMobileBreakpoint, scrollBehavior } from '@theme/utilities';

/**
 * @typedef {Object} ProductListingRefs
 * @property {HTMLElement} [toolbar] Sticky HUD; gets `is-stuck` once it sticks under the header
 * @property {HTMLElement} results Replaced on every filter change
 * @property {HTMLElement} [badge] Active filters count on the "Filtra e ordina" button
 * @property {HTMLElement} [grid] Product grid; load more appends to it
 * @property {HTMLElement} [loadMoreWrap] Progress + load more button, replaced after each page
 * @property {HTMLElement} [loadMore]
 */

/**
 * Refs of the filters drawer (`<overlay-drawer id="filters-drawer">`, moved to <body>).
 * @typedef {Object} FiltersDrawerRefs
 * @property {HTMLElement} [panel]
 * @property {HTMLFormElement} [filtersForm]
 * @property {HTMLElement} [filtersBody] Replaced on every filter change, open groups are kept
 * @property {HTMLElement} [filtersCount] Results count in the submit button
 */

/** Drawer id, also used by the toolbar trigger (`on:click="#filters-drawer/toggle"`) */
const DRAWER_ID = 'filters-drawer';

/**
 * Collection and search results: live filters and sorting through the Section Rendering API,
 * "load more" that appends the next page in place, sticky toolbar state.
 *
 * Markup contract: `data-section-id`, refs above. Filter links (active chips, "Azzera tutto",
 * the drawer's "Azzera") use `on:click="/applyFilterLink"` (or `#<listing id>/applyFilterLink`
 * from the drawer); the load more link `on:click="/loadMore"`; the drawer form
 * `on:change="#<listing id>/onFiltersChange"` and `on:submit="#<listing id>/onFiltersSubmit"`.
 * Every link keeps a real href, so the page works without JavaScript.
 *
 * @extends {Component<ProductListingRefs>}
 */
class ProductListing extends Component {
  requiredRefs = ['results'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;

  /** @type {HTMLElement | null} */
  #sentinel = null;

  /**
   * The filters drawer this listing rendered, captured before another listing can render one.
   * @type {HTMLElement | null}
   */
  #drawer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#drawer = document.getElementById(DRAWER_ID);
    this.#drawer?.addEventListener(ThemeEvents.overlayOpen, this.#onDrawerOpen);
    this.#observeToolbar();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#onFiltersChange.cancel();
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;
    this.#sentinel?.remove();
    this.#sentinel = null;
    this.#drawer?.removeEventListener(ThemeEvents.overlayOpen, this.#onDrawerOpen);
    // The drawer lives in <body>: drop it with its section (theme editor re-renders)
    if (!this.isConnected) this.#drawer?.remove();
    this.#drawer = null;
  }

  /** @returns {FiltersDrawerRefs} */
  get #drawerRefs() {
    const drawer = /** @type {(HTMLElement & { refs?: FiltersDrawerRefs }) | null} */ (this.#drawer);
    return drawer?.refs || {};
  }

  /**
   * Active filter chips, "Azzera tutto", the drawer's "Azzera".
   * @param {MouseEvent} event
   */
  applyFilterLink(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    this.#onFiltersChange.cancel();
    this.#render(link.href);
  }

  /** Any filter or sort input changed: refresh after a short pause */
  onFiltersChange() {
    this.#onFiltersChange();
  }

  /**
   * "Mostra N prodotti": refresh now, then close the drawer and bring the results into view.
   * @param {SubmitEvent} event
   */
  async onFiltersSubmit(event) {
    event.preventDefault();
    this.#onFiltersChange.cancel();
    await this.#render(this.#urlFromForm());
    const drawer = this.#drawer;
    if (drawer && 'close' in drawer && typeof drawer.close === 'function') drawer.close();
    this.refs.results.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
  }

  /**
   * Appends the next page to the grid; the link stays a real fallback.
   * @param {MouseEvent} event
   */
  async loadMore(event) {
    const button = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(button instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');

    try {
      const doc = await this.#fetch(button.href);
      const grid = this.refs.grid;
      const items = Array.from(
        doc.querySelectorAll('product-listing > [ref="results"] > [ref="grid"] > .product-listing__item:not(.product-listing__banner)')
      ).map((item) => /** @type {HTMLElement} */ (document.importNode(item, true)));

      items.forEach((item, index) => {
        item.style.setProperty('--i', String(Math.min(index, 8)));
        item.classList.add('is-new');
        grid?.appendChild(item);
      });

      const wrap = this.refs.loadMoreWrap;
      const nextWrap = doc.querySelector('product-listing > [ref="results"] > [ref="loadMoreWrap"]');
      if (wrap && nextWrap) wrap.replaceWith(document.importNode(nextWrap, true));
      else wrap?.remove();

      const firstLink = items[0]?.querySelector('a[href]');
      if (firstLink instanceof HTMLElement) firstLink.focus({ preventScroll: true });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      button.classList.remove('is-loading');
      showToast(window.Theme.translations.loadMoreError, { type: 'error' });
    }
  }

  #onFiltersChange = debounce(() => {
    this.#render(this.#urlFromForm());
  }, 250);

  /** Phones: every group starts collapsed (sort included), the list stays short and scannable */
  #onDrawerOpen = () => {
    if (!isMobileBreakpoint()) return;
    for (const details of this.#drawer?.querySelectorAll('details') || []) details.open = false;
  };

  /** @returns {string} */
  #urlFromForm() {
    const form = this.#drawerRefs.filtersForm;
    if (!form) return window.location.href;
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form).entries()) {
      if (typeof value === 'string' && value !== '') params.append(key, value);
    }
    return `${form.action.split('?')[0]}?${params.toString()}`;
  }

  /**
   * Fetches this section for `url`, aborting the previous request.
   * @param {string} url
   * @returns {Promise<Document>}
   */
  #fetch(url) {
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    return fetchSection(this.dataset.sectionId || '', { url, signal: controller.signal });
  }

  /**
   * Shows the results for `url` without reloading; falls back to navigating there.
   * @param {string} url
   * @returns {Promise<void>}
   */
  async #render(url) {
    this.classList.add('is-loading');
    try {
      const doc = await this.#fetch(url);
      this.#swap(doc);
      history.replaceState({}, '', url);
      this.classList.remove('is-loading');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.classList.remove('is-loading');
      window.location.href = url;
    }
  }

  /** @param {Document} doc */
  #swap(doc) {
    const nextResults = doc.querySelector('product-listing > [ref="results"]');
    if (nextResults) {
      this.refs.results.replaceChildren(
        ...Array.from(nextResults.childNodes).map((node) => document.importNode(node, true))
      );
    }

    this.#swapFiltersBody(doc);

    const total = doc.querySelector('[data-results-count]')?.getAttribute('data-results-count');
    const count = this.#drawerRefs.filtersCount;
    if (total != null && count) count.textContent = total;

    const badge = this.refs.badge;
    const nextBadge = doc.querySelector('product-listing > [ref="toolbar"] [ref="badge"]');
    if (badge && nextBadge instanceof HTMLElement) {
      badge.textContent = nextBadge.textContent;
      badge.hidden = nextBadge.hidden;
    }
  }

  /**
   * Replaces the filter groups, keeping open groups open and focus on the same input.
   * @param {Document} doc
   */
  #swapFiltersBody(doc) {
    const body = this.#drawerRefs.filtersBody;
    const nextBody = doc.querySelector(`#${DRAWER_ID} [ref="filtersBody"]`);
    if (!body || !nextBody) return;

    const openGroups = Array.from(body.querySelectorAll('details'), (details) => details.open);
    const active = document.activeElement;
    const focused = active instanceof HTMLInputElement && body.contains(active) ? { name: active.name, value: active.value } : null;

    body.replaceChildren(...Array.from(nextBody.childNodes).map((node) => document.importNode(node, true)));

    body.querySelectorAll('details').forEach((details, index) => {
      if (openGroups[index]) details.open = true;
    });

    if (focused) {
      const input = Array.from(body.querySelectorAll('input')).find(
        (input) => input.name === focused.name && (input.type === 'number' || input.value === focused.value)
      );
      input?.focus({ preventScroll: true });
    }
  }

  /** Marks the toolbar `is-stuck` while it sticks under the header */
  #observeToolbar() {
    const toolbar = this.refs.toolbar;
    if (!toolbar || this.#stickyObserver) return;
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    toolbar.before(sentinel);
    this.#sentinel = sentinel;
    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry) toolbar.classList.toggle('is-stuck', !entry.isIntersecting);
      },
      { rootMargin: '-80px 0px 0px 0px' }
    );
    this.#stickyObserver.observe(sentinel);
  }
}

if (!customElements.get('product-listing')) {
  customElements.define('product-listing', ProductListing);
}
